import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bars3Icon, XMarkIcon, TruckIcon } from "@heroicons/react/24/outline";
import { useAuthStore } from "../../store/authStore";
import { useUiStore } from "../../store/uiStore";
import { authService } from "../../services/authService";
import { Button } from "../atoms/Button";
import { ThemeToggle } from "../atoms/ThemeToggle";
import { Avatar } from "../atoms/Avatar";
import { LanguageSwitcher } from "../molecules/LanguageSwitcher";
import logoImage from "../../assets/global-clean-logo.png";

export function Header() {
  const navigate = useNavigate();
  const { user, logout: logoutStore } = useAuthStore();
  const { t } = useTranslation("common");
  const { sidebarCollapsed, toggleSidebar, toggleMobileMenu } = useUiStore();

  const handleLogout = async () => {
    await authService.logout();
    logoutStore();
    navigate("/login");
  };

  const handleSwitchToDelivery = () => {
    localStorage.setItem("staff_view_preference", "delivery");
    window.location.href = "/staff";
  };

  const isStaff = user?.role === "staff";

  if (!user) return null;

  return (
    <header className="bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 sticky top-0 z-40">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left side - Menu button (mobile) / Sidebar toggle (desktop) and logo */}
          <div className="flex items-center gap-3">
            {/* Mobile: Hamburger to open app menu */}
            <button
              onClick={toggleMobileMenu}
              className="md:hidden flex items-center justify-center p-2 rounded-lg text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-800"
              aria-label={t("open_menu")}
            >
              <Bars3Icon className="w-6 h-6" />
            </button>
            {/* Desktop: Sidebar collapse toggle */}
            <button
              onClick={toggleSidebar}
              className="hidden md:flex items-center justify-center p-2 rounded-lg text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-800"
              aria-label={sidebarCollapsed ? t("expand_sidebar") : t("collapse_sidebar")}
            >
              {sidebarCollapsed ? (
                <Bars3Icon className="w-6 h-6" />
              ) : (
                <XMarkIcon className="w-6 h-6" />
              )}
            </button>

            <Link
              to="/dashboard"
              className="flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded"
              aria-label={t("go_to_dashboard")}
            >
              <img
                src={logoImage}
                alt={t("logo_alt")}
                className="h-8 w-auto"
              />
            </Link>
          </div>

          {/* Right side - Theme toggle, view switcher (staff only), user info, logout */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Theme toggle */}
            <ThemeToggle />

            {/* Language switcher (Story 11.2) */}
            <LanguageSwitcher />

            {/* View switcher - Staff in Operations mode only (Story 10-5-8) */}
            {isStaff && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSwitchToDelivery}
                className="flex items-center gap-2 min-h-[40px] min-w-[40px]"
                title={t("switch_to_delivery")}
                aria-label={t("switch_to_delivery")}
              >
                <TruckIcon className="w-4 h-4" />
                <span className="hidden md:inline">{t("delivery")}</span>
              </Button>
            )}

            {/* User info - desktop */}
            <div className="hidden sm:flex items-center gap-3">
              <Avatar name={user.fullName || user.email || user.pseudoLogin || "?"} size="sm" />
              <div className="flex flex-col">
                <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  {user.fullName || t("admin_fallback")}
                </span>
                <span className="text-xs text-neutral-500 dark:text-neutral-400">
                  {user.email ?? user.pseudoLogin ?? ""}
                </span>
              </div>
            </div>

            {/* User avatar - mobile only */}
            <div className="sm:hidden">
              <Avatar name={user.fullName || user.email || user.pseudoLogin || "?"} size="sm" />
            </div>

            {/* Logout button */}
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <span className="hidden sm:inline">{t("logout")}</span>
              <span className="sm:hidden">{t("exit")}</span>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}
